import { useState, useEffect, useContext } from 'react';
import ExamContext from '../context/Exam/ExamContext';
import TenantContext from '../context/Tenant/TenantContext';

export const useExam = (id, employeeId, visitId) => {

    const { getTenant, tenant } = useContext(TenantContext);

    const { examState, getExam, getExamForVisit, seenInduction, seenInductionForVisit, sendVerifyCodeToMail, sendVerifyCodeToVisitMail } = useContext(ExamContext);

    const { exam, isFetching, sending, error, message, alert, verify, } = examState;

    const [questionaryAnswers, setQuestionaryAnswers] = useState([]);

    const [videoPercentage, setVideoPercentage] = useState(0);

    const [wasSeen, setWasSeen] = useState(false);

    const [verifyCode, setVerifyCode] = useState('');

    const [codeWasResend, setCodeWasResend] = useState(false);

    const [feedbackComment, setFeedbackComment] = useState('');

    const inductionValues = employeeId
        ? {
            contractor: exam ? exam.contractor_id : null,
            employee: employeeId,
            ...(feedbackComment.trim() && { feedback: feedbackComment.trim() })
        }
        : {
            visit: visitId,
            induction: id,
            ...(feedbackComment.trim() && { feedback: feedbackComment.trim() })
        };

    const questionaryValues = employeeId
        ? {
            contractor: exam ? exam.contractor_id : null,
            employee: employeeId,
            questionary: questionaryAnswers,
            ...(feedbackComment.trim() && { feedback: feedbackComment.trim() })
        }
        : {
            visit: visitId,
            induction: id,
            questionary: questionaryAnswers,
            ...(feedbackComment.trim() && { feedback: feedbackComment.trim() })
        };

    const setCorrect = (questionId, answerId) => {
        const newData = questionaryAnswers.map((question) => {
            if (question.question === questionId) {
                // Convertir answer a array si no lo es
                const currentAnswers = Array.isArray(question.answer) ? question.answer : (question.answer ? [question.answer] : []);

                // Si la respuesta ya está seleccionada, la removemos
                if (currentAnswers.includes(answerId)) {
                    const newAnswers = currentAnswers.filter(id => id !== answerId);
                    return { ...question, answer: newAnswers };
                } else {
                    // Si no está seleccionada, la agregamos
                    const newAnswers = [...currentAnswers, answerId];
                    return { ...question, answer: newAnswers };
                }
            }
            return question;
        });
        setQuestionaryAnswers(newData);
    };

    const isDisabled = () => {
        const allAnswering = questionaryAnswers.every((questionaryAnswer) => {
            const answers = Array.isArray(questionaryAnswer.answer) ? questionaryAnswer.answer : (questionaryAnswer.answer ? [questionaryAnswer.answer] : []); return answers.length > 0;
        });
        if (exam && exam.questionary) { return !(wasSeen && allAnswering); }
        return !wasSeen;
    };
    const sendExam = () => {
        if (exam.questionary) {
            employeeId
                ? seenInduction({
                    inductionValues,
                    visualizationId: exam.visualization_id,
                    questionaryValues,
                    verificationCode: verifyCode
                })
                : seenInductionForVisit({
                    inductionValues,
                    questionaryValues,
                    verificationCode: verifyCode
                });
        } else {
            employeeId
                ? seenInduction({
                    inductionValues,
                    visualizationId: exam.visualization_id,
                    verificationCode: verifyCode
                })
                : seenInductionForVisit({
                    inductionValues,
                    verificationCode: verifyCode
                });
        }
    };

    const sendEmptyExam = async () => {
        // Crear questionaryValues vacío (todas las respuestas vacías)
        const emptyQuestionaryValues = employeeId
            ? {
                contractor: exam ? exam.contractor_id : null,
                employee: employeeId,
                questionary: exam && exam.questionary 
                    ? exam.questionary.questions.map((question) => ({
                        question: question._id,
                        answer: []
                    }))
                    : [],
                ...(feedbackComment.trim() && { feedback: feedbackComment.trim() })
            }
            : {
                visit: visitId,
                induction: id,
                questionary: exam && exam.questionary 
                    ? exam.questionary.questions.map((question) => ({
                        question: question._id,
                        answer: []
                    }))
                    : [],
                ...(feedbackComment.trim() && { feedback: feedbackComment.trim() })
            };

        if (exam && exam.questionary) {
            if (employeeId) {
                await seenInduction({
                    inductionValues,
                    visualizationId: exam.visualization_id,
                    questionaryValues: emptyQuestionaryValues,
                    verificationCode: verifyCode
                });
            } else {
                await seenInductionForVisit({
                    inductionValues,
                    questionaryValues: emptyQuestionaryValues,
                    verificationCode: verifyCode
                });
            }
        }
    };

    const handleSendVerifyCodeToMail = async () => {
        setVerifyCode('');
        await employeeId ? sendVerifyCodeToMail(id, employeeId) : sendVerifyCodeToVisitMail(visitId, id);
    }

    const reSendCode = async () => {
        await handleSendVerifyCodeToMail();
        if (!error) {
            setCodeWasResend(true);
        }

    }

    const resetQuestionary = () => {
        if (exam && exam.questionary) {
            setQuestionaryAnswers(
                exam.questionary.questions.map((question) => ({
                    question: question._id,
                    answer: [],
                }))
            );
        }
    };

    const handleGetExam = () => {
        if (employeeId) {
            getExam(id, employeeId, verifyCode);
        }
        if (visitId) {
            getExamForVisit(id, visitId, verifyCode);
        }
    }

    useEffect(() => {
        getTenant();
        if (!verify) {
            handleSendVerifyCodeToMail();
        }
    }, []);

    useEffect(() => {
        if (exam) {
            if (exam.questionary) {
                setQuestionaryAnswers(
                    exam.questionary.questions.map((question) => ({
                        question: question._id,
                        answer: [],
                    }))
                );
            }

            const hasVideo = exam.induction.files.some(file => file.type === 'video/mp4');
            if (hasVideo) {
                setVideoPercentage(exam.induction.video_percentage);
            }
        }
    }, [exam]);

    return {
        exam,
        examState,
        tenant,
        questionaryAnswers,
        videoPercentage,
        wasSeen,
        setWasSeen,
        setCorrect,
        isDisabled,
        sendExam,
        sendEmptyExam,
        resetQuestionary,
        setVerifyCode,
        verifyCode,
        verify,
        isFetching,
        error,
        message,
        alert,
        sending,
        handleSendVerifyCodeToMail,
        reSendCode,
        codeWasResend,
        getExam: handleGetExam,
        feedbackComment,
        setFeedbackComment
    };
}; 